import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { FormData } from "0.Mine/0.Components/CRM/Leads/sharedTypes";
import { useSession } from "0.Mine/2.Context/sessionContext";
import { Lead, LeadState, StatusOption } from "0.Mine/3.Interfaces/lead";
import axiosInstance from "0.Mine/axiosInstance";

import { AdditionalParams, QlickupActions, useQlickupAPI } from "../APICall";

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  currentPage: number;
  lastPage: number;
  perPage: number;
}

export const UseHandleLeads = () => {
  const navigate = useNavigate();
  const { session } = useSession();
  const { QlickUpAPI } = useQlickupAPI();

  useEffect(() => {
    if (!session) {
      navigate("/signin", { replace: true });
    }
    if (!session?.user) {
      navigate("/signin", { replace: true });
    }
  });

  const getAllLeads = async (businessId: number, page: number = 1, pageSize: number = 10) => {
    const userid = session.user.id;
    const token = session.user.token;
    const payloadForQuery: Record<string, string> = {
      userid: userid,
      token: token ?? "",
      businessid: businessId.toString() ?? "",
      action: "getall",
      page: page.toString(),
      limit: pageSize.toString(),
    };

    try {
      const queryString = new URLSearchParams(payloadForQuery).toString();
      const response = await axiosInstance.get(`GET/leads.php?${queryString}`);

      if (response.data.success) {
        return {
          data: response.data.leads as Lead[],
          total:
            response.data.pagination?.total || response.data.total || response.data.leads.length,
          currentPage: response.data.pagination?.currentPage || page,
          lastPage:
            response.data.pagination?.lastPage ||
            Math.ceil(
              (response.data.pagination?.total ||
                response.data.total ||
                response.data.leads.length) / pageSize
            ),
          perPage: response.data.pagination?.perPage || pageSize,
        } as PaginatedResponse<Lead>;
      } else {
        return {
          data: [],
          total: 0,
          currentPage: 1,
          lastPage: 1,
          perPage: pageSize,
        } as PaginatedResponse<Lead>;
      }
    } catch (error) {
      console.error(error);
      return {
        data: [],
        total: 0,
        currentPage: 1,
        lastPage: 1,
        perPage: pageSize,
      } as PaginatedResponse<Lead>;
    }
  };

  const editLead = async (businessId: number, updatedLead: Lead) => {
    await QlickUpAPI(
      businessId.toString(),
      "addUpdateLeads",
      updatedLead,
      "leads",
      QlickupActions.POST
    );
  };

  const addLead = async (businessId: number, lead: Lead | Lead[]) => {
    const userid = session.user.id;
    const token = session.user.token;

    const payloadForQuery: Record<string, string> = {
      userid: userid,
      token: token ?? "",
      action: "addUpdateLeads",
      data: JSON.stringify(lead),
      businessid: businessId.toString(),
    };
    const queryString = new URLSearchParams(payloadForQuery).toString();
    try {
      const response = await axiosInstance.post(`POST/leads.php?${queryString}`);

      if (!response.data.success) return 0;

      return response.data.results[0].id as number;
    } catch (error) {
      console.log(error);
      return 0;
    }
  };

  const getLeadStatusOptions = async (businessId: number) => {
    try {
      const response = await QlickUpAPI(
        businessId.toString(),
        "getStatusOptions",
        null,
        "kanban",
        QlickupActions.GET
      );
      return response.statusOptions as StatusOption[];
    } catch (error) {
      console.error(error);
    }
  };

  const createStatusOption = async (businessId: number, statusOption: string) => {
    const additionalParams: AdditionalParams = { label: statusOption };

    try {
      const response = await QlickUpAPI(
        businessId.toString(),
        "createStatusOption",
        null,
        "leads",
        QlickupActions.POST,
        additionalParams
      );
      return response.status_option.id as number;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const deleteStatusOption = async (businessId: number, statusOption: string) => {
    const additionalParams: AdditionalParams = { statusid: statusOption };

    try {
      await QlickUpAPI(
        businessId.toString(),
        "deleteStatusOption",
        null,
        "leads",
        QlickupActions.POST,
        additionalParams
      );
    } catch (error) {
      console.error(error);
    }
  };

  const searchLeadByTerm = async (
    businessId: number,
    filters: { name?: string; company?: string; email?: string },
    page: number = 1,
    pageSize: number = 10
  ) => {
    const additionalParams: AdditionalParams = {
      ...filters,
      page: page.toString(),
      limit: pageSize.toString(),
    };

    const response = await QlickUpAPI(
      businessId.toString(),
      "getfiltered",
      null,
      "leads",
      QlickupActions.GET,
      additionalParams
    );

    if (response.success) {
      return {
        data: response.leads as Lead[],
        total: response.pagination?.total || response.total || response.leads.length,
        currentPage: response.pagination?.currentPage || page,
        lastPage:
          response.pagination?.lastPage ||
          Math.ceil(
            (response.pagination?.total || response.total || response.leads.length) / pageSize
          ),
        perPage: response.pagination?.perPage || pageSize,
      } as PaginatedResponse<Lead>;
    }
    return {
      data: [],
      total: 0,
      currentPage: 1,
      lastPage: 1,
      perPage: pageSize,
    } as PaginatedResponse<Lead>;
  };

  const createLeadCardInformation = async (
    businessId: number,
    cardId: string,
    formData: FormData
  ) => {
    const additionalParams: AdditionalParams = {
      cardId: cardId,
    };

    await QlickUpAPI(
      businessId.toString(),
      "addUpdateLeads",
      formData,
      "leads",
      QlickupActions.POST,
      additionalParams
    );
  };

  const deleteLead = async (businessId: number, leadId: string) => {
    const additionalParams: AdditionalParams = {
      leadid: leadId,
    };

    const response = await QlickUpAPI(
      businessId.toString(),
      "deleteLead",
      null,
      "leads",
      QlickupActions.POST,
      additionalParams
    );

    return response.success as boolean;
  };

  return {
    getAllLeads,
    addLead,
    editLead,
    getLeadStatusOptions,
    createStatusOption,
    deleteStatusOption,
    searchLeadByTerm,
    createLeadCardInformation,
    deleteLead,
  };
};
/**
 * Maps a raw lead object from the CRM UI to the Lead interface
 * @param rawLead The raw lead object from the UI
 * @returns A properly formatted Lead object
 */
export const mapRawLeadToLead = (businessId: number, rawLead: Lead) => {
  const lead: Lead = {
    id: 0,
    name: rawLead.name,
    company: rawLead.company || "",
    email: rawLead.email || "",
    phone: rawLead.phone,
    lastContact: rawLead.lastContact ? new Date(rawLead.lastContact) : new Date(),
    value: rawLead.value || "0",
    notes: rawLead.notes || "",
    state: mapStatusToLeadState(rawLead.state),
    businessid: businessId || 0,
    leadSource: rawLead.leadSource || "",
  };
  console.log(lead);
  return lead;
};

/**
 * Maps a string status to the corresponding LeadState enum value
 * @param status The status string
 * @returns The corresponding LeadState enum value
 */
const mapStatusToLeadState = (status: string): LeadState => {
  switch (status) {
    case "New Lead":
      return LeadState.New;
    case "Qualified":
      return LeadState.Qualified;
    case "Proposal Sent":
      return LeadState.Proposal;
    case "Negotiation":
      return LeadState.Negotiation;
    case "Closed Won":
      return LeadState.Won;
    default:
      return LeadState.New;
  }
};

/**
 * Maps a Lead object back to the raw format expected by the CRM UI
 * @param lead The Lead object
 * @returns A raw lead object for the UI
 */
export const mapLeadToRawLead = (lead: Lead): any => {
  return {
    id: lead.id.toString(),
    lead: ["", lead.name], // No avatar URL available
    company: lead.company,
    email: lead.email,
    status: lead.state,
    lastContact: lead.lastContact,
    hasBorder: true,
  };
};

export const useHandleLeadTemp = () => {
  const { QlickUpAPI } = useQlickupAPI();

  const getLeadDetails = async (
    businessId: number,
    cardId: string,
    detailToSearch: "status" | "duedate" | "value" | "probability"
  ) => {
    try {
      const additionalParams: AdditionalParams = {
        cardid: cardId,
        detailToSearch: detailToSearch,
      };

      const response = await QlickUpAPI(
        businessId.toString(),
        "getLeadDetails",
        null,
        "leads",
        QlickupActions.GET,
        additionalParams
      );
      console.log(response);

      return response.lead.value as any;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const postLeadDetails = async (
    businessId: number,
    cardId: string,
    detailToPost: "status" | "duedate" | "value" | "probability",
    value: string | number | Date
  ) => {
    const additionalParams: AdditionalParams = {
      cardid: cardId,
      detailToSearch: detailToPost,
      val: value,
    };

    const response = await QlickUpAPI(
      businessId.toString(),
      "postLeadDetails",
      null,
      "leads",
      QlickupActions.POST,
      additionalParams
    );
    console.log(response);
  };

  return { getLeadDetails, postLeadDetails };
};
